import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

import { BRUSH_PRESETS } from "@excalidraw/element";

import type {
  BrushParams,
  BrushPreset,
  BrushSetId,
  BrushTipType,
} from "@excalidraw/element";

import { Island } from "../Island";
import {
  BrushIcon,
  FillCrossHatchIcon,
  FillHachureIcon,
  LibraryIcon,
  TrashIcon,
  adjustmentsIcon,
  nibIcon,
  pencilIcon,
  resetRotationIcon,
} from "../icons";

import { BrushPreview } from "./BrushPreview";
import {
  deleteCustomBrush,
  loadCustomBrushes,
  saveCustomBrush,
} from "./brushLibrary";

import "./BrushPanel.scss";

import type { AppState, UIAppState } from "../../types";

const SETS: { id: BrushSetId; label: string; icon: React.ReactNode }[] = [
  { id: "sketching", label: "Sketch", icon: pencilIcon },
  { id: "inking", label: "Ink", icon: nibIcon },
  { id: "painting", label: "Paint", icon: BrushIcon },
  { id: "materials", label: "Materials", icon: FillCrossHatchIcon },
  { id: "airbrush", label: "Airbrush", icon: FillHachureIcon },
  { id: "custom", label: "Custom", icon: LibraryIcon },
];

const TIPS: { id: BrushTipType; label: string }[] = [
  { id: "round", label: "Round" },
  { id: "grain", label: "Grain" },
  { id: "bristle", label: "Bristle" },
  { id: "fiber", label: "Fiber" },
  { id: "spray", label: "Spray" },
];

/** slider rows for the tune view — every BrushParams field the user can edit */
const TUNE_FIELDS: {
  key: keyof BrushParams;
  label: string;
  group: "Tip" | "Stamping" | "Dynamics";
}[] = [
  { key: "hardness", label: "Hardness", group: "Tip" },
  { key: "grainIntensity", label: "Grain", group: "Tip" },
  { key: "strandCurl", label: "Fiber curl", group: "Tip" },
  { key: "fuzz", label: "Fuzz", group: "Tip" },
  { key: "spacing", label: "Spacing", group: "Stamping" },
  { key: "scatter", label: "Scatter", group: "Stamping" },
  { key: "rotationJitter", label: "Rotation jitter", group: "Stamping" },
  { key: "opacityJitter", label: "Opacity jitter", group: "Stamping" },
  { key: "sizePressure", label: "Pressure → size", group: "Dynamics" },
  { key: "opacityPressure", label: "Pressure → opacity", group: "Dynamics" },
  { key: "taper", label: "Taper", group: "Dynamics" },
  { key: "streamline", label: "Streamline", group: "Dynamics" },
];

/** CSS custom property driving the slider's filled-track color stop */
const progressStyle = (value: number, min: number, max: number) => ({
  ["--slider-progress" as string]: `${((value - min) / (max - min)) * 100}%`,
});

const Slider = ({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}) => (
  <label className="brush-panel__slider">
    <div className="brush-panel__slider-row">
      <span>{label}</span>
      <span className="brush-panel__slider-value">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      style={progressStyle(value, min, max)}
      onChange={(e) => onChange(e.target.valueAsNumber)}
    />
  </label>
);

export const BrushPanel = ({
  appState,
  setAppState,
  mode,
}: {
  appState: UIAppState;
  setAppState: React.Component<any, AppState>["setState"];
  mode: "brush" | "eraser";
}) => {
  const [activeSet, setActiveSet] = useState<BrushSetId>("sketching");
  const [customBrushes, setCustomBrushes] = useState(loadCustomBrushes);
  const [tuning, setTuning] = useState<BrushPreset | null>(null);
  // the untouched preset the current tune session started from, so Reset
  // has something to restore to that isn't the (already edited) `tuning`
  const [original, setOriginal] = useState<BrushPreset | null>(null);

  // free-drag position (offset from the panel's docked spot). Resets when the
  // panel unmounts (i.e. on switching away from brush/eraser).
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const posRef = useRef(pos);
  posRef.current = pos;

  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // The brush list only covers the lower part of the panel, so a wheel over
  // the tab bar / mode toggle / padding would otherwise sail past the panel
  // to whatever is behind it. Route every wheel over the panel into the list
  // so hovering anywhere on the window scrolls the window, and nothing else.
  //
  // Registered natively rather than via onWheel because React binds wheel
  // listeners passively, which makes preventDefault() a no-op (same reason
  // App.tsx registers its own wheel handler outside React).
  //
  // Re-runs when swapping between the list and tune views: those are two
  // separate Islands, so the old node is unmounted and the listener has to
  // move to the new one. `isTuning` (not `tuning`) keeps this from
  // re-attaching on every slider tick.
  const isTuning = tuning !== null;
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      // Keep the wheel out of App's container-level handler, which pans/zooms
      // the scene for any HTMLCanvasElement target — and every brush swatch
      // in this list IS a <canvas> (see BrushPreview). Without this, wheeling
      // over a swatch scrolls the scene behind the panel and preventDefault()s
      // the list's own scroll. stopPropagation doesn't cancel the default
      // action, so native scrolling below still works.
      event.stopPropagation();

      const list = listRef.current;
      // nothing to scroll → let the event through untouched
      if (!list || list.scrollHeight <= list.clientHeight) {
        return;
      }
      // inside the list the browser already scrolls it natively (which keeps
      // its smooth/compositor-driven feel), and `overscroll-behavior:
      // contain` keeps the delta from chaining out — leave it alone
      if ((event.target as HTMLElement).closest(".brush-panel__list")) {
        return;
      }
      // deltaY is in lines when deltaMode is DOM_DELTA_LINE (Firefox's
      // default for a mouse wheel) and pages for DOM_DELTA_PAGE; treating
      // either as pixels would scroll ~3px per tick instead of a full line
      const scale =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? list.clientHeight
          : 1;
      list.scrollTop += event.deltaY * scale;
      event.preventDefault();
    };
    panel.addEventListener("wheel", onWheel, { passive: false });
    return () => panel.removeEventListener("wheel", onWheel);
  }, [isTuning]);

  // drag from anywhere on the panel that isn't an interactive control —
  // there is no visible drag handle.
  // The scrollable regions are excluded too: this handler preventDefault()s
  // pointerdown, which cancels the browser's native touch/pen scroll, so
  // without the exclusion a finger-drag over the brush list moves the whole
  // panel instead of scrolling it.
  const startDrag = (e: React.PointerEvent) => {
    if (
      e.button !== 0 ||
      (e.target as HTMLElement).closest(
        "button, input, label, [role='button'], [role='tab'], .brush-panel__list, .brush-panel__sliders",
      )
    ) {
      return;
    }
    e.preventDefault();
    const base = posRef.current;
    const startX = e.clientX;
    const startY = e.clientY;
    const move = (ev: PointerEvent) => {
      setPos({
        x: base.x + (ev.clientX - startX),
        y: base.y + (ev.clientY - startY),
      });
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  // match the left toolbar's height so the panel's bottom edge lines up with
  // it (both start at the same top offset — see LayerUI.scss)
  const [toolbarHeight, setToolbarHeight] = useState<number | null>(null);
  useEffect(() => {
    const toolbar = document.querySelector(".App-toolbar-container");
    if (!toolbar) {
      return;
    }
    const observer = new ResizeObserver(([entry]) =>
      setToolbarHeight(entry.contentRect.height),
    );
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, []);

  const islandStyle = {
    ...(pos.x || pos.y
      ? { transform: `translate(${pos.x}px, ${pos.y}px)` }
      : null),
    ...(toolbarHeight ? { height: `${toolbarHeight}px` } : null),
  };

  const isEraser = mode === "eraser";

  const currentParams = isEraser
    ? appState.currentEraserBrush
    : appState.currentItemBrush;
  const currentSize = isEraser
    ? appState.currentEraserSize
    : appState.currentItemBrushSize;
  // Procreate-style: previews always render as white strokes directly on
  // the dark panel, regardless of the currently selected stroke color
  const previewColor = "#ffffff";

  const brushes = useMemo(
    () =>
      activeSet === "custom"
        ? customBrushes
        : BRUSH_PRESETS.filter((b) => b.set === activeSet),
    [activeSet, customBrushes],
  );

  const applyBrush = (params: BrushParams, size?: number) => {
    if (isEraser) {
      if (size !== undefined) {
        setAppState({ currentEraserBrush: params, currentEraserSize: size });
      } else {
        setAppState({ currentEraserBrush: params });
      }
    } else if (size !== undefined) {
      setAppState({ currentItemBrush: params, currentItemBrushSize: size });
    } else {
      setAppState({ currentItemBrush: params });
    }
  };

  const setSize = (size: number) => {
    if (isEraser) {
      setAppState({ currentEraserSize: size });
    } else {
      setAppState({ currentItemBrushSize: size });
    }
  };

  const paramsEqual = (a: BrushParams, b: BrushParams) =>
    JSON.stringify(a) === JSON.stringify(b);

  const sizeSlider = (
    <Slider
      label="Size"
      value={currentSize}
      min={1}
      max={128}
      format={(v) => `${v}px`}
      onChange={setSize}
    />
  );

  // The eraser has exactly two functions — pixel and object — plus a width.
  // No brush sets, presets or tuning: erasing isn't painting.
  if (isEraser) {
    return (
      <Island
        ref={panelRef}
        className="brush-panel brush-panel--eraser"
        padding={2}
        // no toolbar-matching height: this panel has three rows, not a list
        style={{ ...islandStyle, height: undefined }}
        onPointerDown={startDrag}
      >
        <div className="brush-panel__mode hig-segmented">
          {(
            [
              ["pixel", "Pixel", "Rub out part of a brush stroke"],
              ["object", "Object", "Remove whole elements"],
            ] as const
          ).map(([id, label, title]) => (
            <button
              key={id}
              title={title}
              className={clsx("hig-segmented__item", {
                active: appState.eraserMode === id,
              })}
              onClick={() => setAppState({ eraserMode: id })}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="brush-panel__eraser-preview">
          <span
            style={{
              width: Math.min(currentSize, 96),
              height: Math.min(currentSize, 96),
            }}
          />
        </div>
        <div className="brush-panel__sliders">{sizeSlider}</div>
      </Island>
    );
  }

  if (tuning) {
    const upd = (key: keyof BrushParams, value: number | boolean | string) => {
      const next = { ...tuning, params: { ...tuning.params, [key]: value } };
      setTuning(next);
      applyBrush(next.params);
    };
    const resetToOriginal = () => {
      if (!original) {
        return;
      }
      setTuning(original);
      applyBrush(original.params, original.size);
    };
    const usesStrands =
      tuning.params.tip === "bristle" || tuning.params.tip === "fiber";

    let lastGroup = "";
    return (
      <Island
        ref={panelRef}
        className="brush-panel brush-panel--tune"
        padding={2}
        style={islandStyle}
        onPointerDown={startDrag}
      >
        <div className="brush-panel__header">
          <button
            className="brush-panel__back"
            onClick={() => setTuning(null)}
            aria-label="Back"
          >
            ←
          </button>
          <span className="brush-panel__header-title">{tuning.name}</span>
          <button
            className="brush-panel__reset"
            onClick={resetToOriginal}
            aria-label={`Reset ${tuning.name}`}
            title="Reset to default"
          >
            {resetRotationIcon}
          </button>
        </div>
        <BrushPreview
          params={tuning.params}
          size={currentSize}
          color={previewColor}
        />
        <div className="brush-panel__sliders">
          {sizeSlider}
          <div className="brush-panel__group">Tip shape</div>
          <div className="brush-panel__chips">
            {TIPS.map((t) => (
              <button
                key={t.id}
                className={clsx({ active: tuning.params.tip === t.id })}
                onClick={() => upd("tip", t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          {usesStrands && (
            <Slider
              label="Strands"
              value={tuning.params.strandCount}
              min={0}
              max={64}
              step={1}
              format={(v) => `${v}`}
              onChange={(v) => upd("strandCount", v)}
            />
          )}
          <label className="brush-panel__toggle">
            <span>Tip follows stroke</span>
            <input
              type="checkbox"
              checked={tuning.params.followPath}
              onChange={(e) => upd("followPath", e.target.checked)}
            />
          </label>
          {TUNE_FIELDS.map((f) => {
            const header =
              f.group !== lastGroup ? (
                <div className="brush-panel__group" key={f.group}>
                  {f.group}
                </div>
              ) : null;
            lastGroup = f.group;
            const value = tuning.params[f.key] as number;
            return (
              <div key={f.key}>
                {header}
                <Slider
                  label={f.label}
                  value={value}
                  min={0}
                  max={1}
                  step={0.01}
                  format={(v) => `${Math.round(v * 100)}%`}
                  onChange={(v) => upd(f.key, v)}
                />
              </div>
            );
          })}
        </div>
        <div className="brush-panel__tune-actions">
          <button
            className="brush-panel__btn brush-panel__btn--primary"
            onClick={() => applyBrush(tuning.params)}
          >
            Use
          </button>
          <button
            className="brush-panel__btn brush-panel__btn--ghost"
            onClick={() => {
              const id = `custom-${Date.now().toString(36)}`;
              const saved = {
                ...tuning,
                id,
                name: `${tuning.name} copy`,
                set: "custom" as const,
                size: currentSize,
              };
              setCustomBrushes(saveCustomBrush(saved));
              setActiveSet("custom");
              setTuning(null);
            }}
          >
            Save as new brush
          </button>
        </div>
      </Island>
    );
  }

  return (
    <Island
      ref={panelRef}
      className="brush-panel"
      padding={2}
      style={islandStyle}
      onPointerDown={startDrag}
    >
      {/* Apple HIG segmented control — skin comes from `.hig-segmented`
          (panelTokens.scss), this only adds icon-over-label layout */}
      <div className="brush-panel__tabs hig-segmented" role="tablist">
        {SETS.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={activeSet === s.id}
            title={s.label}
            className={clsx("brush-panel__tab", "hig-segmented__item", {
              active: activeSet === s.id,
            })}
            onClick={() => setActiveSet(s.id)}
          >
            {s.icon}
            <span>{s.label}</span>
          </button>
        ))}
      </div>
      <div className="brush-panel__body">
        <div className="brush-panel__list" ref={listRef}>
          {brushes.map((b) => {
            const active = paramsEqual(currentParams, b.params);
            return (
              <div
                key={b.id}
                role="button"
                tabIndex={0}
                aria-pressed={active}
                className={clsx("brush-panel__card", { active })}
                onClick={() => applyBrush(b.params, b.size)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    applyBrush(b.params, b.size);
                  }
                }}
              >
                <div className="brush-panel__card-label">
                  <span className="brush-panel__card-name">{b.name}</span>
                  <span className="brush-panel__card-actions">
                    <button
                      aria-label={`Tune ${b.name}`}
                      title="Adjust brush"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOriginal(b);
                        setTuning(b);
                        applyBrush(b.params, b.size);
                      }}
                    >
                      {adjustmentsIcon}
                    </button>
                    {b.set === "custom" && (
                      <button
                        aria-label={`Delete ${b.name}`}
                        title="Delete brush"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCustomBrushes(deleteCustomBrush(b.id));
                        }}
                      >
                        {TrashIcon}
                      </button>
                    )}
                  </span>
                </div>
                <BrushPreview
                  params={b.params}
                  size={b.size}
                  color={previewColor}
                />
              </div>
            );
          })}
          {brushes.length === 0 && (
            <div className="brush-panel__empty">
              No custom brushes yet — open any brush with the sliders icon and
              “Save as new brush”.
            </div>
          )}
        </div>
      </div>
    </Island>
  );
};
